import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { IMAGES } from "@/constants/images";
import PageHero from "@/components/public/PageHero";
import ProductCard from "@/components/public/ProductCard";
import EmptyState from "@/components/public/EmptyState";
import SectionHeading from "@/components/public/SectionHeading";
import CTASection from "@/components/public/CTASection";
import Reveal from "@/components/public/Reveal";
import DownloadButton from "@/components/public/DownloadButton";
import { useLang } from "@/context/LangContext";
import { useSettings } from "@/context/SettingsContext";

export default function OurCoffee() {
  const { t } = useLang();
  const { settings } = useSettings();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try { const res = await api.get("/products"); setProducts(res.data); } catch (e) {} finally { setLoading(false); }
    })();
  }, []);

  return (
    <div>
      <PageHero eyebrow="Green Coffee Catalog" title="Indonesian Arabica Green Coffee" sub="A professional green coffee catalog focused on Indonesian Arabica — including our featured Ateng coffee — sourced, stored and prepared for international buyers." image={IMAGES.greenBeans} />

      <section className="mx-auto max-w-[1400px] px-4 py-16 sm:px-8 lg:px-12">
        <div className="grid grid-cols-1 items-center gap-10 lg:grid-cols-2 lg:gap-16">
          <Reveal>
            <div className="aspect-[4/3] overflow-hidden rounded-2xl"><img src={IMAGES.beansScoop} alt="Indonesian Arabica green coffee" className="h-full w-full object-cover" /></div>
          </Reveal>
          <Reveal delay={120}>
            <div className="mb-3 font-mono text-xs font-semibold uppercase tracking-[0.25em] text-amber-brand">Main Category</div>
            <h2 className="font-display text-3xl font-extrabold tracking-tight text-forest-dark sm:text-4xl">Indonesian Arabica</h2>
            <p className="mt-4 font-body text-base leading-relaxed text-espresso/75">
              Our catalog centers on Indonesian Arabica green coffee. Every lot is described with transparent specifications — origin, process, grade, screen size and moisture — so buyers know exactly what they are sourcing. Specifications are only shown when confirmed for each lot.
            </p>
            <div className="mt-6 flex flex-wrap gap-2">
              {["Ateng", "Mandheling", "Gayo", "Java Preanger"].map((tag) => (
                <span key={tag} className="rounded-full border border-forest/15 bg-white px-3.5 py-1.5 font-mono text-[11px] uppercase tracking-wider text-forest">{tag}</span>
              ))}
            </div>
            {settings.catalog_pdf && (
              <div className="mt-8">
                <DownloadButton url={settings.catalog_pdf} label={t("cta.catalog")} filename="warehouse-coffee-catalog.pdf" testId="ourcoffee-catalog-download" />
              </div>
            )}
          </Reveal>
        </div>
      </section>

      <section className="bg-beige py-16 lg:py-24">
        <div className="mx-auto max-w-[1400px] px-4 sm:px-8 lg:px-12">
          <SectionHeading eyebrow="Current Catalog" title="Available Coffees" />
          <div className="mt-12">
            {loading ? (
              <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">{[0,1,2].map(i => <div key={i} className="h-80 animate-pulse rounded-xl bg-forest/5" />)}</div>
            ) : products.length === 0 ? (
              <EmptyState message="Coffee availability is currently being updated." testId="ourcoffee-empty" />
            ) : (
              <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
                {products.map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
              </div>
            )}
          </div>
        </div>
      </section>
      <CTASection />
    </div>
  );
}
